'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { DateInputWithCalendar } from '@/components/ui/date-input-with-calendar';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TimeUnitInput,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
} from '@/components/ui/time-unit-input';
import { CalendarIcon, Clock } from 'lucide-react';
import type { Availability, Match } from '@/types/tournament.model';
import { PlayersNamesList } from '@/components/tournament/PlayerNamesList';
import { AvailabilityStatusIcon } from '@/components/tournament/fixture/AvailabilityStatusIcon';
import { StatusIcon } from '@/components/tournament/fixture/StatusIcon';
import { TeamsAvailabilityComparison } from '@/components/tournament/fixture/match/TeamsAvailabilityComparison';
import {
  fitsAvailability,
  fitsTournamentSchedule,
} from '@/utils/availability-fit';

interface MatchDateTimeEditModalProps {
  // Only `startDate` is read below (to seed the date/time inputs). Narrowed to this instead of
  // the full `Match` so the shared grid's `GridMatch` — which has no `id`/`endDate`/etc. — can
  // be passed in without fabricating the rest of a `Match`. Both existing call sites already
  // pass a full `Match`, which stays assignable to this narrower shape.
  match: Pick<Match, 'startDate'>;
  team1Name: string;
  team2Name: string;
  team1Object?: Match['team1'];
  team2Object?: Match['team2'];
  team1Availability?: Availability[];
  team2Availability?: Availability[];
  tournamentScheduleAvailability?: Availability[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (startTime: string) => void;
  isSubmitting: boolean;
}

export function MatchDateTimeEditModal({
  match,
  team1Name,
  team2Name,
  team1Object,
  team2Object,
  team1Availability,
  team2Availability,
  tournamentScheduleAvailability,
  open,
  onOpenChange,
  onSave,
  isSubmitting,
}: MatchDateTimeEditModalProps) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');

  // Initialize form with current match data
  useEffect(() => {
    if (open && match.startDate) {
      const startDate = new Date(match.startDate);

      // Format date for input (YYYY-MM-DD) using local time to avoid UTC offset shifting the day
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const day = String(startDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      setDate(dateStr);

      // Format time for input (HH:MM)
      const timeStr = startDate.toTimeString().slice(0, 5);
      setTime(timeStr);
      const [initialHour, initialMinute] = timeStr.split(':');
      setHour(initialHour ?? '');
      setMinute(initialMinute ?? '');
    }
  }, [open, match.startDate]);

  useEffect(() => {
    if (hour && minute) {
      setTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
    }
  }, [hour, minute]);

  const dateAsDate = date
    ? (() => {
        const [y, m, d] = date.split('-').map(Number);
        return new Date(y, m - 1, d, 0, 0, 0, 0);
      })()
    : null;
  const minDateToday = (() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  })();

  const handleSave = () => {
    if (!date || !time) return;

    // Create a Date object with the selected date and time
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);

    // Format in LocalDateTime format (without timezone info)
    // Backend expects: YYYY-MM-DDTHH:mm:ss
    const formattedYear = String(year);
    const formattedMonth = String(month).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    const dateTimeString = `${formattedYear}-${formattedMonth}-${formattedDay}T${formattedHours}:${formattedMinutes}:00`;

    onSave(dateTimeString);
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form
    setDate('');
    setTime('');
    setHour('');
    setMinute('');
  };

  const isFormValid = date && time;

  const team1Ok = fitsAvailability(team1Availability, date, time);
  const team2Ok = fitsAvailability(team2Availability, date, time);
  const scheduleOk = fitsTournamentSchedule(
    tournamentScheduleAvailability,
    date,
    time,
  );

  const getPlayerInitials = (playerName: string) => {
    const parts = playerName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
      );
    }
    return (parts[0]?.slice(0, 2) || 'P').toUpperCase();
  };

  const renderTeamRow = (
    label: string,
    teamObj: Match['team1'] | Match['team2'] | undefined,
    fallbackName: string,
    ok: boolean | null,
  ) => {
    const players =
      teamObj && typeof teamObj !== 'string' ? teamObj.players : undefined;

    return (
      <div className="rounded-md border bg-white">
        <div className="flex items-center justify-between gap-3 p-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex -space-x-2">
              {[0, 1].map((idx) => {
                const player = players?.[idx];
                const imgUrl = player?.profileImageUrl || undefined;
                const initials = getPlayerInitials(player?.name || `P${idx + 1}`);
                return imgUrl ? (
                  <img
                    key={idx}
                    src={imgUrl}
                    alt=""
                    className="h-8 w-8 rounded-full border-2 border-white object-cover"
                  />
                ) : (
                  <div
                    key={idx}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-slate-600 text-xs font-semibold text-white"
                  >
                    {initials}
                  </div>
                );
              })}
            </div>

            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500">{label}</p>
              <PlayersNamesList players={players} fallbackName={fallbackName} />
            </div>
          </div>

          {ok !== null && (
            <div className="flex items-center gap-1">
              {scheduleOk === false ? (
                <StatusIcon
                  variant="error"
                  ariaLabel="Horario fuera de horarios del torneo"
                  message="La nueva fecha/hora está fuera de los bloques de horarios disponibles del torneo."
                />
              ) : (
                <AvailabilityStatusIcon ok={ok} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[95vw] max-w-[1100px] flex-col overflow-hidden bg-white [&>button.absolute]:z-40 [&>button.absolute]:bg-white">
        {/* Fijo arriba en todos los tamaños: es hijo directo del DialogContent, así que el `-mx-6
            -mt-6` cancela su `p-6` y el borde llega a los extremos. Antes había una segunda copia
            de todo esto para mobile, metida adentro del área de scroll (`px-0`), donde ese mismo
            `-mx-6` no cancelaba nada y la dejaba 48px más ancha que el modal. */}
        <div className="-mx-6 -mt-6 border-b bg-white px-6 pt-6 shadow-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Editar fecha y horario
            </DialogTitle>
            <DialogDescription>
              Modifica la fecha y horario del partido entre{' '}
              <span className="font-semibold">{team1Name}</span> y{' '}
              <span className="font-semibold">{team2Name}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="match-date" className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Fecha del partido
                </Label>
                <DateInputWithCalendar
                  id="match-date"
                  value={dateAsDate}
                  onChange={(d) =>
                    setDate(
                      d
                        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                        : '',
                    )
                  }
                  minDate={minDateToday}
                  placeholder="dd/mm/aaaa"
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="match-hour" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Horario del partido
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <TimeUnitInput
                    id="match-hour"
                    value={hour}
                    onChange={setHour}
                    options={HOUR_OPTIONS}
                    maxValue={23}
                    placeholder="Hora"
                    ariaLabel="Horas"
                  />
                  <span className="select-none pointer-events-none text-muted-foreground" aria-hidden>
                    :
                  </span>
                  <TimeUnitInput
                    id="match-minute"
                    value={minute}
                    onChange={setMinute}
                    options={MINUTE_OPTIONS}
                    maxValue={59}
                    placeholder="Min"
                    ariaLabel="Minutos"
                  />
                </div>
              </div>
            </div>

            {date && time && (
              <div className="rounded-md bg-blue-50 p-3 text-sm">
                <p className="text-blue-700 leading-snug">
                  <span className="font-medium text-blue-900">
                    Nueva fecha y horario:{' '}
                  </span>
                  <span className="break-words">
                    {new Date(`${date}T${time}`).toLocaleString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </p>
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              {renderTeamRow('Equipo 1', team1Object, team1Name, team1Ok)}
              {renderTeamRow('Equipo 2', team2Object, team2Name, team2Ok)}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 py-4">
            <TeamsAvailabilityComparison
              team1Availability={team1Availability}
              team2Availability={team2Availability}
              schedule={tournamentScheduleAvailability}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isFormValid || isSubmitting}
            className="min-w-[100px]"
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
